import React, { useState } from 'react';
import Toaster from 'components/Toaster';
import {
  IconTile,
  AddButton,
  SegmentedFilter,
  ActionIcon,
  ConfirmModal,
} from 'components/v3';
import { topicIconName } from '../../LearningHub/topicIcons';
import TopicFormModal from '../components/TopicFormModal';
import VariantPill, { MODE_LABELS } from '../components/VariantPill';
import {
  useGetAdminTopics,
  useCreateTopic,
  useUpdateTopic,
  useDeleteTopic,
} from '../gql';
import {
  PageHeader,
  PageTitle,
  PageDescription,
  Grid,
  TopicCard,
  TopicHeader,
  TopicTitle,
  TopicDescription,
  TopicFooter,
  TopicCount,
} from '../StyledComponents';

const MODE_FILTER_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'discover', label: 'Ideas & Tips' },
  { key: 'howto', label: 'How To Do It' },
];

const TopicsPage = () => {
  const [modeFilter, setModeFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [deletingTopic, setDeletingTopic] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toaster, setToaster] = useState({ isOpen: false });

  const mode = modeFilter !== 'all' ? modeFilter : undefined;
  const { topics } = useGetAdminTopics(mode);
  const createTopic = useCreateTopic();
  const updateTopic = useUpdateTopic();
  const deleteTopic = useDeleteTopic();

  const handleSave = async (data) => {
    const input = {
      name: data.name,
      slug: data.slug,
      description: data.description,
      mode: data.mode,
      icon: data.icon,
    };

    const isEditing = !!editingTopic;
    const result = isEditing
      ? await updateTopic({ id: editingTopic.id, ...input })
      : await createTopic(input);

    if (result) {
      setToaster({
        isOpen: true,
        type: 'success',
        title: isEditing ? 'Topic Updated' : 'Topic Created',
        message: `"${input.name}" has been ${isEditing ? 'updated' : 'added'}.`,
      });
      setShowModal(false);
      setEditingTopic(null);
    } else {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: `Failed to ${
          isEditing ? 'update' : 'create'
        } topic. Please try again.`,
      });
    }
  };

  const handleEdit = (topic) => {
    setEditingTopic(topic);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingTopic(null);
    setShowModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    const name = deletingTopic.name;
    const ok = await deleteTopic(deletingTopic.id);
    setIsDeleting(false);
    setDeletingTopic(null);
    setToaster(
      ok
        ? {
            isOpen: true,
            type: 'success',
            title: 'Topic Deleted',
            message: `"${name}" has been deleted.`,
          }
        : {
            isOpen: true,
            type: 'error',
            title: 'Error',
            message: 'Failed to delete topic. Please try again.',
          }
    );
  };

  return (
    <>
      <PageHeader>
        <div>
          <PageTitle>Topics</PageTitle>
          <PageDescription>Organize videos into categories</PageDescription>
        </div>
        <AddButton title="Add Topic" onClick={handleAdd} />
      </PageHeader>

      <div style={{ marginBottom: 24 }}>
        <SegmentedFilter
          value={modeFilter}
          onChange={setModeFilter}
          options={MODE_FILTER_OPTIONS}
        />
      </div>

      <Grid>
        {topics.map((topic) => (
          <TopicCard key={topic.id}>
            <TopicHeader>
              <IconTile
                icon={topicIconName(topic.icon)}
                size={40}
                iconSize={20}
              />
              <VariantPill
                variant={topic.mode}
                label={MODE_LABELS[topic.mode]}
              />
            </TopicHeader>
            <TopicTitle>{topic.name}</TopicTitle>
            <TopicDescription>{topic.description}</TopicDescription>
            <TopicFooter>
              <TopicCount>{topic.video_count || 0} videos</TopicCount>
              <div style={{ display: 'flex', gap: 3 }}>
                <ActionIcon
                  icon="square-pen"
                  title="Edit"
                  onClick={() => handleEdit(topic)}
                />
                <ActionIcon
                  icon="trash-2"
                  title="Delete"
                  danger
                  onClick={() => setDeletingTopic(topic)}
                />
              </div>
            </TopicFooter>
          </TopicCard>
        ))}
      </Grid>

      {showModal && (
        <TopicFormModal
          topic={editingTopic}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingTopic(null);
          }}
        />
      )}

      <ConfirmModal
        open={!!deletingTopic}
        title="Delete Topic?"
        message={`Are you sure you want to delete ${deletingTopic?.name}? This action cannot be undone.`}
        loading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletingTopic(null)}
      />

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
      />
    </>
  );
};

export default TopicsPage;
